import { mapDesigns } from "@mono/mapDesigns"
import { socket } from "@/utils/utils"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import { useNavigate } from "react-router-dom"
import PropertyBidCard from "@/Components/PropertyBigCard"
import Money from "@/Components/Money"
import ModalButton from "@/Components/ModalButton"
import GameTimer from "@/Components/GameTimer"
import Modal from "@/Components/Modal"
import { useTimerTickSound } from "@/Hooks/useTimerTickSound"
import { useGameTimings } from "@/Hooks/useGameTimings"

interface BuyOrAuctionModalProps {}

const BuyOrAuctionModal: React.FC<BuyOrAuctionModalProps> = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const { cardDecisionTime } = useGameTimings()
  // This modal is only mounted while the decision is ours to make, so the
  // decision clock ticks for as long as it is on screen — and stops if that
  // clock is frozen.
  useTimerTickSound(true, "cardDecisionTimeLeft")
  const modalData = useAppSelector((state) => state.global.modalData)
  const myMapId = myGame?.settings?.mapId
  const myMap = mapDesigns.find((map) => map.id === myMapId)
  const activePropertyId = modalData.buyOrAuction?.propertyId
  const property = myMap?.cells.find((cell) => cell.order === activePropertyId)
  const nav = useNavigate()
  const onClickOnBuy = () => {
    socket.emit("game.buyProperty", myGame!.id, property!.order)
  }
  const onClickOnAuction = () => {
    dispatch(setModalData({ buyOrAuction: { visible: false } }))
    socket.emit("auction.started", myGame!.id, property!.order)
  }
  return (
    <Modal
      data-testid="game.buy-or-auction-modal"
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
      title="Buy or Auction"
      subtitle="Buy it now, or send it to auction"
      contentClassName="flex gap-4 px-8 pb-8"
    >
      <PropertyBidCard cell={property as ICell}></PropertyBidCard>
      <div className="flex flex-col justify-between text-white">
        <div className="w-full flex justify-center mb-4">
          <GameTimer
            timerKey="cardDecisionTimeLeft"
            totalTime={cardDecisionTime}
            size="md"
            showLabel={true}
            labelText="Decision Time"
          />
        </div>
        <div className="flex flex-col gap-4 items-center">
          <div
            className="font-poppins text-4xl text-center font-semibold"
            data-testid="game.buy-or-auction.name"
          >
            {property?.propertyDetails?.name}
          </div>
          <Money
            money={property?.propertyDetails?.price || 0}
            className="gap-2 font-poppins text-2xl "
            iconClass="h-2/3"
          ></Money>
        </div>
        <div className="flex w-full flex-col gap-4">
          <ModalButton
            color="green"
            onClick={onClickOnBuy}
            data-testid="game.buy-or-auction.buy-btn"
            className="w-full min-w-[10rem]"
          >
            <div className="flex items-center justify-center gap-2">
              <i className="fa-cart-shopping fa-solid text-white fa-lg"></i>
              <span className="text-white font-medium font-poppins text-lg">
                Buy
              </span>
            </div>
          </ModalButton>
          <ModalButton
            color="yellow"
            onClick={onClickOnAuction}
            data-testid="game.buy-or-auction.auction-btn"
            className="w-full min-w-[10rem]"
          >
            <div className="flex items-center justify-center gap-2">
              <i className="fa-gavel fa-solid text-white fa-lg"></i>
              <span className="text-white font-medium font-poppins text-lg">
                Auction
              </span>
            </div>
          </ModalButton>
        </div>
      </div>
    </Modal>
  )
}

export default BuyOrAuctionModal
